import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Post } from '@/hooks/usePosts';
import { useCategories } from '@/hooks/useCategories';

interface FeaturedCarouselProps {
  posts: Post[];
}

export default function FeaturedCarousel({ posts }: FeaturedCarouselProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { getCategoryById } = useCategories();

  const goTo = useCallback((index: number) => {
    setCurrent(index);
  }, []);

  const next = useCallback(() => {
    setCurrent(c => (c + 1) % posts.length);
  }, [posts.length]);

  const prev = useCallback(() => {
    setCurrent(c => (c - 1 + posts.length) % posts.length);
  }, [posts.length]);

  useEffect(() => {
    if (posts.length <= 1 || paused) return;
    timerRef.current = setInterval(next, 5500);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [next, posts.length, paused]);

  if (posts.length === 0) return null;

  const getCategoryLabel = (post: Post) => {
    const id = post.categories?.[0];
    if (!id) return '';
    return getCategoryById(id)?.label ?? id;
  };

  return (
    <section
      className="w-full bg-background-50 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Top label row */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-8 md:pt-14 pb-4 md:pb-5">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-2 text-primary-500 text-[10px] md:text-xs font-bold tracking-widest uppercase">
            <i className="ri-star-fill text-xs md:text-sm"></i>
            Artigos em Destaque
          </span>
          {posts.length > 1 && (
            <span className="text-foreground-400 text-[10px] md:text-xs tabular-nums">
              {current + 1} / {posts.length}
            </span>
          )}
        </div>
      </div>

      {/* Slider viewport */}
      <div className="relative w-full overflow-hidden">

        {/* Track — each slide is min-w-full so track stretches to n×100% */}
        <div
          className="flex"
          style={{
            transform: `translateX(-${current * 100}%)`,
            transition: 'transform 0.55s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'transform',
          }}
        >
          {posts.map((post) => (
            <div key={post.id} className="min-w-full">
              <div className="max-w-7xl mx-auto px-4 md:px-6 pb-8">
                <div className="flex flex-col md:flex-row items-center gap-6 md:gap-14">

                  {/* Image */}
                  <div className="w-full md:w-[46%] h-[180px] sm:h-[240px] md:h-[380px] rounded-xl overflow-hidden flex-shrink-0 group/img">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover object-top scale-100 group-hover/img:scale-105 transition-transform duration-500 ease-out"
                    />
                  </div>

                  {/* Text */}
                  <div className="flex-1 min-w-0 text-center md:text-left">
                    <span className="inline-block bg-primary-100 text-primary-600 text-[10px] md:text-xs font-bold tracking-widest uppercase px-2.5 py-0.5 md:px-3 md:py-1 rounded-full mb-3 md:mb-4">
                      {getCategoryLabel(post)}
                    </span>

                    <h2 className="font-heading font-bold text-foreground-950 text-lg sm:text-xl md:text-3xl lg:text-4xl leading-tight mb-2 md:mb-4">
                      {post.title}
                    </h2>

                    <p className="text-foreground-600 text-xs sm:text-sm md:text-base leading-relaxed mb-4 md:mb-5 line-clamp-2 md:line-clamp-3">
                      {post.subtitle}
                    </p>

                    <p className="text-foreground-400 text-[10px] md:text-xs mb-4 md:mb-6 flex items-center justify-center md:justify-start gap-1.5">
                      <i className="ri-time-line"></i>
                      {post.readTime}
                    </p>

                    <Link
                      to={`/post/${post.slug}`}
                      className="group/link inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-xs sm:text-sm font-bold px-5 py-2.5 rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap"
                    >
                      Ler artigo
                      <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform duration-200"></i>
                    </Link>
                  </div>

                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Prev / Next Arrows */}
        {posts.length > 1 && (
          <>
            <button
              onClick={prev}
              aria-label="Anterior"
              className="absolute left-2 md:left-5 top-[35%] md:top-1/2 -translate-y-1/2 z-10 w-7 h-7 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-50/95 border border-background-200 text-foreground-700 hover:bg-primary-500 hover:border-primary-500 hover:text-white transition-all duration-200 cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-base md:text-xl"></i>
            </button>
            <button
              onClick={next}
              aria-label="Próximo"
              className="absolute right-2 md:right-5 top-[35%] md:top-1/2 -translate-y-1/2 z-10 w-7 h-7 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-50/95 border border-background-200 text-foreground-700 hover:bg-primary-500 hover:border-primary-500 hover:text-white transition-all duration-200 cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-base md:text-xl"></i>
            </button>
          </>
        )}
      </div>

      {/* Dot indicators */}
      {posts.length > 1 && (
        <div className="flex items-center justify-center gap-2 pt-3 md:pt-4 pb-8 md:pb-10">
          {posts.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Ir para slide ${i + 1}`}
              className="h-1.5 md:h-2 rounded-full cursor-pointer transition-all duration-300 ease-out"
              style={{
                width: i === current ? '1.5rem' : '0.4rem',
                backgroundColor: i === current
                  ? 'oklch(var(--primary-500))'
                  : 'oklch(var(--background-300))',
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}